import {
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  OAuthProvider,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  type User,
  type UserCredential,
  verifyPasswordResetCode
} from 'firebase/auth'
import { auth } from './config'

const googleProvider = new GoogleAuthProvider()
const appleProvider = new OAuthProvider('apple.com')

export async function signInWithGoogle(): Promise<UserCredential> {
  try {
    const result = await signInWithPopup(auth, googleProvider)
    return result
  } catch (error) {
    console.error('Google sign-in error:', error)
    throw error
  }
}

export async function signInWithApple(): Promise<UserCredential> {
  try {
    const result = await signInWithPopup(auth, appleProvider)
    return result
  } catch (error) {
    console.error('Apple sign-in error:', error)
    throw error
  }
}

// Signs in an EXISTING account only. Never creates one — new accounts are minted
// solely through the invite signup flow via signUpWithEmail. An unknown email
// surfaces as auth/user-not-found or auth/invalid-credential for the login page to handle.
export async function signInWithEmail(email: string, password: string): Promise<UserCredential> {
  try {
    return await signInWithEmailAndPassword(auth, email, password)
  } catch (error) {
    console.error('Email sign-in error:', error)
    throw error
  }
}

// Creates a new email/password account. Used ONLY by the invite signup flow; an
// existing email throws auth/email-already-in-use for the form to surface as "log in instead".
export async function signUpWithEmail(email: string, password: string): Promise<UserCredential> {
  try {
    return await createUserWithEmailAndPassword(auth, email, password)
  } catch (error) {
    console.error('Account creation error:', error)
    throw error
  }
}

export async function reloadCurrentUser(): Promise<User | null> {
  const user = auth.currentUser
  if (!user) return null
  await user.reload()
  return auth.currentUser
}

export async function signOut(): Promise<void> {
  try {
    await firebaseSignOut(auth)
  } catch (error) {
    console.error('Sign-out error:', error)
    throw error
  }
}

export async function resetPassword(email: string): Promise<void> {
  try {
    await sendPasswordResetEmail(auth, email)
  } catch (error) {
    console.error('Password reset error:', error)
    throw error
  }
}

export async function verifyResetCode(oobCode: string): Promise<string> {
  return verifyPasswordResetCode(auth, oobCode)
}

export async function resetPasswordWithCode(oobCode: string, newPassword: string): Promise<void> {
  await confirmPasswordReset(auth, oobCode, newPassword)
}
